// Brief status — ONE display model, used identically by the Briefs list rows,
// the Board cards, the Board column accents and the brief-detail header.
// (Brief-detail refactor §7.)
//
// Status was previously styled at least four ways: text-with-a-tick for
// Awarded, a solid red pill for Overdue, grey outline pills for Sent/Closed,
// and a teal pill for Awaiting. Everything now resolves through here, so a
// card's pill and its column can't disagree.
//
// Colour comes from the shared A2 semantic tones (see StatusPill) — a status
// declares a MEANING, never a hex. Nothing in this file hard-codes colour.
//
// OVERDUE IS A MODIFIER, NOT A STATUS. An overdue brief keeps its real status
// pill and additionally shows a red deadline + a small flag. It is DERIVED
// (status is live && deadline < now) and never stored as a status value —
// that's what stops the old "red date AND a red OVERDUE pill" duplication.

export const BRIEF_STATUS = {
  draft: {
    key: 'draft', label: 'Draft', tone: 'neutral',
    hint: 'Not sent yet',
    live: false,          // can't be overdue — it hasn't gone out
  },
  sent: {
    key: 'sent', label: 'Sent', tone: 'pending',
    hint: 'Out, awaiting',
    live: true,
  },
  responses_in: {
    key: 'responses_in', label: 'Responses In', tone: 'progress',
    hint: 'Replies arriving',
    live: true,
  },
  awarded: {
    key: 'awarded', label: 'Awarded', tone: 'confirmed',
    hint: 'Supplier chosen',
    live: false,
    tick: true,           // the one status that earns an icon instead of a dot
  },
  closed: {
    key: 'closed', label: 'Closed', tone: 'neutral',
    hint: 'Done / archived',
    live: false,
  },
};

export const BRIEF_STATUS_ORDER = ['draft', 'sent', 'responses_in', 'awarded', 'closed'];

// Map whatever the row actually carries onto the display set. The DB has
// grown a few spellings over time ('awaiting_responses', 'complete'…), so
// normalise here rather than at every call site.
const ALIASES = {
  awaiting: 'sent',
  awaiting_response: 'sent',
  awaiting_responses: 'responses_in',
  responses: 'responses_in',
  responded: 'responses_in',
  in_progress: 'responses_in',
  complete: 'closed',
  completed: 'closed',
  archived: 'closed',
  won: 'awarded',
};

export function normaliseStatus(raw) {
  if (!raw) return 'draft';
  const k = String(raw).toLowerCase().replace(/[\s-]+/g, '_');
  if (BRIEF_STATUS[k]) return k;
  return ALIASES[k] || 'draft';
}

// Derived — never stored. A brief is overdue when it's still live (out for
// response) and its deadline has passed. An awarded or closed brief is not
// overdue, however old its deadline.
export function isOverdue(rawStatus, deadline, now = new Date()) {
  const status = BRIEF_STATUS[normaliseStatus(rawStatus)];
  if (!status?.live || !deadline) return false;
  const d = deadline instanceof Date ? deadline : new Date(deadline);
  if (Number.isNaN(d.getTime())) return false;
  return d.getTime() < now.getTime();
}

// The full display model for a brief — what every surface should render from.
export function briefStatusView(rawStatus, deadline, now = new Date()) {
  const key = normaliseStatus(rawStatus);
  const status = BRIEF_STATUS[key];
  return {
    ...status,
    overdue: isOverdue(rawStatus, deadline, now),
  };
}
